import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { Contact, ContactLogic } from '@/modules/contact';
import type { ContactAction } from '@/modules/contact/reducer';
import type { ContactList } from '@/modules/contact';
import type { MessageDraft } from '@/modules/message';
import type { MessageIntent } from '@/modules/template';
import type { Topic } from '@/modules/topic/types';
import { checkTopicAwareness, type TopicAwarenessMap } from '@/services/meprfo';
import { TopicAwarenessLogic } from '@snowball/meprfo';
import {
  createContactEnrichment,
  todayISO,
  type ContactEnrichment,
} from '@/hooks/useSnowballSync';

// Module-level cache: survives across wizard sessions within the same SPA
// lifecycle, eliminating the OPRF round-trips that cause the 👋 flash when
// the same contact is shared with a second time.
const sessionAwarenessCache = new Map<string, TopicAwarenessMap>();

export function _testOnly_clearTopicAwarenessCache(): void {
  sessionAwarenessCache.clear();
}

/**
 * Who knows about the topic once `leg` has been sent: the local bump a sent
 * leg makes before the next server answer arrives.
 *
 * An intro is the first the recipients have heard of this topic, so it marks
 * them introduced — by this sender and by anyone. A reminder reaches people
 * who already know, so it only bumps their count. This mirrors the backend's
 * GetTopicAwareness, which the cache stands in for until the next lookup.
 */
function awarenessAfterLeg(
  awarenessMap: TopicAwarenessMap,
  leg: { header: Pick<MessageDraft['header'], 'intent' | 'recipients'> }
): TopicAwarenessMap {
  const updatedAwareness = new Map(awarenessMap);
  const countsAsIntro = leg.header.intent === 'intro';
  leg.header.recipients.forEach(contact => {
    if (!contact.snowballId) return;
    const existing = updatedAwareness.get(contact.snowballId);
    updatedAwareness.set(contact.snowballId, {
      introducedByCurrentUser:
        countsAsIntro || !!existing?.introducedByCurrentUser,
      introducedByAnyone: countsAsIntro || !!existing?.introducedByAnyone,
      reminderCount:
        (existing?.reminderCount ?? 0) +
        (leg.header.intent === 'reminder' ? 1 : 0),
      lastUpdatedAt:
        leg.header.intent === 'update'
          ? new Date().toISOString()
          : existing?.lastUpdatedAt,
      needsUpdate:
        leg.header.intent === 'update' ? false : !!existing?.needsUpdate,
    });
  });
  return updatedAwareness;
}

/**
 * Record a leg sent outside any sharing session — from the outbox — in this
 * session's awareness cache, so the next share of the topic starts from it
 * just as it would had the leg been sent in the flow.
 */
export function recordSentLegAwareness(
  topicId: string,
  intent: MessageIntent,
  recipients: readonly Contact[]
): void {
  sessionAwarenessCache.set(
    topicId,
    awarenessAfterLeg(sessionAwarenessCache.get(topicId) ?? new Map(), {
      header: { intent, recipients: [...recipients] },
    })
  );
}

export interface UseTopicAwarenessParams {
  topicId: string;
  /** Null until the topic fetch resolves; every lookup is keyed by its id. */
  topic: Topic | null;
  /** The live recipient selection, from ContactStateContext. */
  recipients: ContactList;
  dispatch: React.Dispatch<ContactAction>;
  /**
   * The session's confirmed-recipient writer. A sent leg confirms the people
   * it reached at the same moment it makes them aware, so `recordSentLeg`
   * writes both rather than leaving the caller to remember the second.
   */
  setConfirmedContactIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export interface TopicAwareness {
  /** Who already knows about this topic, keyed by server userId. */
  awarenessMap: TopicAwarenessMap;
  /**
   * Whether the awareness check has answered for the recipients as they are
   * now. False from the moment the selection changes until the check for it
   * returns or fails, so routing read in between can mistake someone who
   * already knows for someone new.
   */
  recipientsAwarenessChecked: boolean;
  /**
   * Ask about contacts the recipient list has scrolled into view (#581).
   * Best-effort and deduplicated by phone, so a screenful looked at twice
   * costs one round-trip.
   */
  requestAwarenessFor: (contacts: readonly Contact[]) => void;
  /**
   * Cache each newly-resolved server userId back onto its contact's
   * snowballId. Exposed because the update flow's PSI match resolves ids by
   * the same path and writes them back the same way.
   */
  enrichResolvedIds: (
    contacts: readonly Contact[],
    resolvedIds: ReadonlyMap<string, string>
  ) => void;
  /** Record a leg the sender has confirmed sending. */
  recordSentLeg: (leg: MessageDraft) => void;
}

/**
 * Who has already heard about this topic, and the bookkeeping that keeps that
 * answer current: the server check as recipients change, the per-screenful
 * lookups the recipient list asks for, and the local bump a sent leg makes
 * before the next server answer arrives.
 *
 * The map is keyed by server userId, which a contact without a Snowball
 * account never gets, so every lookup here also writes resolved ids back onto
 * the contacts that produced them.
 */
export function useTopicAwareness({
  topicId,
  topic,
  recipients,
  dispatch,
  setConfirmedContactIds,
}: UseTopicAwarenessParams): TopicAwareness {
  const [awarenessMap, setAwarenessMap] = useState<TopicAwarenessMap>(
    () => new Map(sessionAwarenessCache.get(topicId))
  );

  /**
   * Cache each newly-resolved server userId back onto its contact's snowballId.
   * The awareness map is keyed by server userId while the introduced-state lookup
   * keys off contact.snowballId — so without this write-back a freshly-added
   * contact (no cached id) never matches an existing intro, even one sent
   * earlier by an unauthenticated user. Routed through the shared
   * ENRICH_CONTACTS reducer path (useSnowballSync).
   */
  const enrichResolvedIds = useCallback(
    (
      contacts: readonly Contact[],
      resolvedIds: ReadonlyMap<string, string>
    ) => {
      if (resolvedIds.size === 0) return;
      const today = todayISO();
      const payload = contacts
        .map(c => {
          const phone = ContactLogic.toContactLike(c).phone as string;
          const resolved = resolvedIds.get(phone);
          if (!resolved || resolved === c.snowballId) return null;
          return createContactEnrichment(
            c.localId,
            resolved,
            c.snowballId,
            today
          );
        })
        .filter((u): u is ContactEnrichment => u !== null);
      if (payload.length > 0) {
        dispatch({ type: 'ENRICH_CONTACTS', payload });
      }
    },
    [dispatch]
  );

  /**
   * Monotonically merge an awareness response into the session cache, so a
   * transient empty server response never overwrites confirmed intro state
   * from this session — and a partial answer (one screenful, #581) only ever
   * adds to what is already known.
   */
  const mergeAwarenessIntoCache = useCallback(
    (cacheKey: string, fresh: TopicAwarenessMap) => {
      const cached: TopicAwarenessMap =
        sessionAwarenessCache.get(cacheKey) ?? new Map();
      const merged: TopicAwarenessMap = new Map(fresh);
      cached.forEach((cachedEntry, userId) => {
        const freshEntry = merged.get(userId);
        merged.set(
          userId,
          freshEntry
            ? TopicAwarenessLogic.merge(freshEntry, cachedEntry)
            : cachedEntry
        );
      });
      setAwarenessMap(merged);
      sessionAwarenessCache.set(cacheKey, merged);
    },
    []
  );

  // Phones already asked about this topic, so a screenful scrolled back into
  // view costs nothing. Keyed by phone rather than by contact id because the
  // awareness map is keyed by server userId, which a contact who has no
  // Snowball account never gets — keying on the answer would make every such
  // contact look permanently uncached.
  const awarenessAskedPhones = useRef<Set<string>>(new Set());
  useEffect(() => {
    awarenessAskedPhones.current = new Set();
  }, [topicId]);

  const requestAwarenessFor = useCallback(
    (contacts: readonly Contact[]) => {
      const cacheKey = topic?.id;
      if (!cacheKey) return;
      const pending: Contact[] = [];
      const pendingPhones: string[] = [];
      for (const contact of contacts) {
        if (!ContactLogic.isReachable(contact)) continue;
        const phone = ContactLogic.toContactLike(contact).phone;
        if (typeof phone !== 'string' || phone === '') continue;
        if (awarenessAskedPhones.current.has(phone)) continue;
        awarenessAskedPhones.current.add(phone);
        pending.push(contact);
        pendingPhones.push(phone);
      }
      if (pending.length === 0) return;
      void (async () => {
        try {
          const { awareness, resolvedIds } = await checkTopicAwareness(
            cacheKey,
            pending.map(c => ContactLogic.toContactLike(c))
          );
          enrichResolvedIds(pending, resolvedIds);
          mergeAwarenessIntoCache(cacheKey, awareness);
        } catch {
          // Best-effort: forget the attempt so scrolling back re-asks, rather
          // than leaving these rows unexplained for the rest of the session.
          for (const phone of pendingPhones) {
            awarenessAskedPhones.current.delete(phone);
          }
        }
      })();
    },
    [topic?.id, enrichResolvedIds, mergeAwarenessIntoCache]
  );

  const prevRecipientsLength = useRef(0);
  // Signature of the recipient phone set last sent to the awareness check.
  // Caching a resolved snowballId back onto a contact (below) mutates
  // recipients and re-triggers this effect; the phone set is unchanged by that
  // write-back, so comparing against it lets us skip the redundant round-trip
  // and only re-check when recipients actually change (added/removed/edited).
  const lastAwarenessSignature = useRef<string | null>(null);
  // Signature of the recipient phone set the check last answered for,
  // successfully or not. A failed check leaves what is known as it was, and
  // the recipients can still be sent to on that.
  const [answeredSignature, setAnsweredSignature] = useState<string | null>(
    null
  );
  const recipientsSignature = useMemo(
    () =>
      JSON.stringify(
        recipients.map(c => ContactLogic.toContactLike(c).phone).sort()
      ),
    [recipients]
  );
  const recipientsAwarenessChecked =
    recipients.length === 0 || answeredSignature === recipientsSignature;
  useEffect(() => {
    if (recipients.length === 0) {
      // Only clear when the user explicitly removes all recipients (going from
      // non-empty → empty). Don't clear on initial mount or when the topic
      // state transitions from null → loaded — those are not user-driven resets
      // and would wipe the awareness cache before the user adds any contacts.
      prevRecipientsLength.current = 0;
      return;
    }
    if (!topic) return;
    // Fire immediately when recipients first appear; debounce incremental edits.
    const wasEmpty = prevRecipientsLength.current === 0;
    prevRecipientsLength.current = recipients.length;
    const delay = wasEmpty ? 0 : 400;
    const handle = window.setTimeout(async () => {
      const contactLikes = recipients.map(c => ContactLogic.toContactLike(c));
      const signature = JSON.stringify(contactLikes.map(cl => cl.phone).sort());
      try {
        // Skip the awareness round-trip when the recipient phone set is
        // unchanged since the last successful check — e.g. when this effect
        // re-fired only because we cached a resolved snowballId back below.
        if (signature === lastAwarenessSignature.current) {
          setAnsweredSignature(signature);
          return;
        }

        const { awareness: awarenessMapResponse, resolvedIds } =
          await checkTopicAwareness(topic.id, contactLikes);

        enrichResolvedIds(recipients, resolvedIds);
        mergeAwarenessIntoCache(topic.id, awarenessMapResponse);
        // These phones are now known to the session cache, so a screenful that
        // scrolls past them doesn't ask again (#581).
        for (const like of contactLikes) {
          if (typeof like.phone === 'string') {
            awarenessAskedPhones.current.add(like.phone);
          }
        }
        // Record the signature only after success, so a failed check retries
        // on the next trigger instead of being suppressed by this guard.
        lastAwarenessSignature.current = signature;
        setAnsweredSignature(signature);
      } catch {
        // On error, preserve existing state rather than wiping it.
        setAnsweredSignature(signature);
      }
    }, delay);
    return () => window.clearTimeout(handle);
  }, [topic, recipients, enrichResolvedIds, mergeAwarenessIntoCache]);

  /**
   * Fold a leg the sender has confirmed sending into what the session knows:
   * its recipients are now aware of the topic, and they are now confirmed. The
   * server will say the same on the next lookup — this is what keeps the list
   * honest until then.
   */
  const recordSentLeg = useCallback(
    (leg: MessageDraft) => {
      const updatedAwareness = awarenessAfterLeg(awarenessMap, leg);
      setAwarenessMap(updatedAwareness);
      sessionAwarenessCache.set(topicId, updatedAwareness);
      setConfirmedContactIds(prev => {
        const next = new Set(prev);
        leg.header.recipients.forEach(c => next.add(c.localId));
        return next;
      });
    },
    [awarenessMap, topicId, setConfirmedContactIds]
  );

  return {
    awarenessMap,
    recipientsAwarenessChecked,
    requestAwarenessFor,
    enrichResolvedIds,
    recordSentLeg,
  };
}
